<template>
  <div class="table-container">
    <el-table :data="data" v-loading="loading" style="width: 100%" border>
      <el-table-column prop="id" :label="$t('UpgradeApplyManagement.serialNumber')" width="80" align="center"/>
      
      <el-table-column prop="applicantId" :label="$t('UpgradeApplyManagement.applicantId')" width="120" align="center"/>
      
      <el-table-column prop="applicantEmail" :label="$t('UpgradeApplyManagement.applicantEmail')" min-width="180" show-overflow-tooltip/>
      
      <el-table-column prop="applyReason" :label="$t('UpgradeApplyManagement.applyReason')" min-width="200" show-overflow-tooltip/>
      
      <el-table-column prop="applicantStatus" :label="$t('UpgradeApplyManagement.applicantStatus')" width="120" align="center">
        <template #default="scope">
          <el-tag :type="getStatusType(scope.row.applicantStatus)">
            {{ scope.row.statusText }}
          </el-tag>
        </template>
      </el-table-column>
      
      <el-table-column prop="applyTime" :label="$t('UpgradeApplyManagement.applyTime')" width="180" align="center">
        <template #default="scope">
          {{ formatDateTime(scope.row.applyTime) }}
        </template>
      </el-table-column>
      
      <el-table-column prop="reviewerEmail" :label="$t('UpgradeApplyManagement.reviewerEmail')" min-width="180" show-overflow-tooltip/>
      
      <el-table-column prop="reviewer" :label="$t('UpgradeApplyManagement.reviewer')" width="120" align="center"/>
      
      <el-table-column prop="reviewTime" :label="$t('UpgradeApplyManagement.reviewTime')" width="180" align="center">
        <template #default="scope">
          {{ scope.row.reviewTime ? formatDateTime(scope.row.reviewTime) : '-' }}
        </template>
      </el-table-column>
      
      <el-table-column prop="reviewComment" :label="$t('UpgradeApplyManagement.reviewComment')" min-width="150" show-overflow-tooltip>
        <template #default="scope">
          {{ scope.row.reviewComment || '-' }}
        </template>
      </el-table-column>
      
      <el-table-column :label="$t('UpgradeApplyManagement.operation')" width="200" align="center" fixed="right">
        <template #default="scope">
          <!-- 待审核状态：显示同意和拒绝按钮 -->
          <div v-if="scope.row.applicantStatus === 'pending'" class="action-buttons">
            <el-button 
              @click="handleApprove(scope.row)" 
              type="success" 
              size="small"
              :loading="scope.row.approving"
              class="approve-btn" v-if="hasPermission('diffUpgradeApplyRecord.check')">
              <el-icon><Check /></el-icon>
              {{ $t('UpgradeApplyManagement.approve') }}
            </el-button>
            <el-button 
              @click="handleReject(scope.row)" 
              type="danger" 
              size="small"
              :loading="scope.row.rejecting"
              class="reject-btn" v-if="hasPermission('diffUpgradeApplyRecord.check')">
              <el-icon><Close /></el-icon>
              {{ $t('UpgradeApplyManagement.reject') }}
            </el-button>
            <span v-else>-</span>
          </div>
          
          <!-- 已审核状态：显示状态标签和查看按钮 -->
          <div v-else class="status-display">
            <el-tag 
              :type="scope.row.applicantStatus === 'approved' ? 'success' : 'danger'" 
              size="small"
              class="status-tag">
              {{ scope.row.applicantStatus === 'approved' ? $t('UpgradeApplyManagement.statusApproved') : $t('UpgradeApplyManagement.statusRejected') }}
            </el-tag>
            <el-button 
              @click="handleView(scope.row)" 
              type="info" 
              size="small"
              class="view-btn">
              <el-icon><View /></el-icon>
              {{ $t('UpgradeApplyManagement.view') }}
            </el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <!-- 审核对话框 -->
    <el-dialog :model-value="reviewDialogVisible" :title="getDialogTitle()" width="600px" :show-close="true" @close="closeReviewDialog" @update:model-value="reviewDialogVisible = $event">
      <el-form :model="reviewForm" :rules="reviewRules" ref="reviewFormRef" label-width="120px">
        <el-form-item :label="$t('UpgradeApplyManagement.applicantId')">
          <el-input v-model="currentApplication.applicantId" disabled />
        </el-form-item>
        <el-form-item :label="$t('UpgradeApplyManagement.applicantEmail')">
          <el-input v-model="currentApplication.applicantEmail" disabled />
        </el-form-item>
        <el-form-item :label="$t('UpgradeApplyManagement.applyReason')">
          <el-input v-model="currentApplication.applyReason" type="textarea" :rows="3" disabled />
        </el-form-item>
        <el-form-item :label="$t('UpgradeApplyManagement.applicantStatus')">
          <el-input v-model="currentApplication.statusText" disabled />
        </el-form-item>
        <el-form-item :label="$t('UpgradeApplyManagement.reviewResult')" prop="reviewResult">
          <el-select v-model="reviewForm.reviewResult" style="width: 100%" :disabled="reviewForm.reviewResult !== ''">
            <el-option :label="$t('UpgradeApplyManagement.statusApproved')" value="approved" />
            <el-option :label="$t('UpgradeApplyManagement.statusRejected')" value="rejected" />
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('UpgradeApplyManagement.reviewComment')" prop="reviewComment">
          <el-input 
            v-model="reviewForm.reviewComment" 
            type="textarea" 
            :rows="4" 
            :placeholder="$t('UpgradeApplyManagement.reviewCommentPlaceholder')"
            maxlength="200"
            show-word-limit
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="closeReviewDialog" type="default">{{ $t('UpgradeApplyManagement.cancel') }}</el-button>
        <el-button @click="submitReview" :loading="reviewLoading" type="primary">{{ $t('UpgradeApplyManagement.confirmReview') }}</el-button>
      </template>
    </el-dialog>

    <!-- 查看详情对话框 -->
    <el-dialog :model-value="viewDialogVisible" :title="$t('UpgradeApplyManagement.viewApplication')" width="600px" :show-close="true" @close="closeViewDialog" @update:model-value="viewDialogVisible = $event">
      <el-form :model="currentApplication" label-width="120px">
        <el-form-item :label="$t('UpgradeApplyManagement.applicantId')">
          <el-input v-model="currentApplication.applicantId" disabled />
        </el-form-item>
        <el-form-item :label="$t('UpgradeApplyManagement.applyReason')">
          <el-input v-model="currentApplication.applyReason" type="textarea" :rows="3" disabled />
        </el-form-item>
        <el-form-item :label="$t('UpgradeApplyManagement.applicantStatus')">
          <el-input v-model="currentApplication.statusText" disabled />
        </el-form-item>
        <el-form-item :label="$t('UpgradeApplyManagement.applyTime')">
          <el-input :value="formatDateTime(currentApplication.applyTime)" disabled />
        </el-form-item>
        <el-form-item :label="$t('UpgradeApplyManagement.reviewerEmail')">
          <el-input v-model="currentApplication.reviewerEmail" disabled />
        </el-form-item>
        <el-form-item :label="$t('UpgradeApplyManagement.reviewer')">
          <el-input v-model="currentApplication.reviewer" disabled />
        </el-form-item>
        <el-form-item :label="$t('UpgradeApplyManagement.reviewTime')">
          <el-input :value="currentApplication.reviewTime ? formatDateTime(currentApplication.reviewTime) : '-'" disabled />
        </el-form-item>
        <el-form-item :label="$t('UpgradeApplyManagement.reviewComment')">
          <el-input v-model="currentApplication.reviewComment" type="textarea" :rows="3" disabled />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="closeViewDialog" type="default">{{ $t('UpgradeApplyManagement.close') }}</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ElMessage } from 'element-plus';
import { reviewDiffUpgradeApply } from '@/api/api';
import dialogMixin from '@/mixins/dialogMixin';

export default {
  mixins: [dialogMixin],
  props: {
    data: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  mounted() {
    this.loadSubPermissions();
  },
  data() {
    return {
      reviewDialogVisible: false,
      viewDialogVisible: false,
      currentApplication: {},
      reviewForm: {
        reviewResult: '',
        reviewComment: ''
      },
      reviewRules: {
        reviewResult: [
          { required: true, message: this.$t('UpgradeApplyManagement.reviewResultRequired'), trigger: 'change' }
        ],
        reviewComment: [
          { required: true, message: this.$t('UpgradeApplyManagement.reviewCommentRequired'), trigger: 'blur' },
          { min: 5, message: this.$t('UpgradeApplyManagement.reviewCommentMinLength'), trigger: 'blur' }
        ]
      },
      reviewLoading: false
    };
  },
  methods: {
    getStatusType(status) {
      const typeMap = {
        'pending': 'warning',
        'urgent': 'danger',
        'normal': 'info',
        'approved': 'success',
        'rejected': 'danger'
      };
      return typeMap[status] || 'info';
    },

    formatDateTime(dateTime) {
      if (!dateTime) return '-';
      const date = new Date(dateTime);
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    },

    getDialogTitle() {
      if (this.reviewForm.reviewResult === 'approved') {
        return this.$t('UpgradeApplyManagement.approveApplication');
      } else if (this.reviewForm.reviewResult === 'rejected') {
        return this.$t('UpgradeApplyManagement.rejectApplication');
      }
      return this.$t('UpgradeApplyManagement.reviewApplication');
    },

    handleApprove(row) {
      this.currentApplication = { ...row };
      this.reviewForm = {
        reviewResult: 'approved',
        reviewComment: ''
      };
      this.reviewDialogVisible = true;
    },

    handleReject(row) {
      this.currentApplication = { ...row };
      this.reviewForm = {
        reviewResult: 'rejected',
        reviewComment: ''
      };
      this.reviewDialogVisible = true;
    },

    handleReview(row) {
      this.currentApplication = { ...row };
      this.reviewForm = {
        reviewResult: '',
        reviewComment: ''
      };
      this.reviewDialogVisible = true;
    },

    handleView(row) {
      this.currentApplication = { ...row };
      this.viewDialogVisible = true;
    },

    closeReviewDialog() {
      this.reviewDialogVisible = false;
      this.currentApplication = {};
      this.reviewForm = {
        reviewResult: '',
        reviewComment: ''
      };
    },

    closeViewDialog() {
      this.viewDialogVisible = false;
      this.currentApplication = {};
    },

    async submitReview() {
      try {
        // 验证表单
        if (!this.$refs.reviewFormRef) {
          ElMessage.error(this.$t('UpgradeApplyManagement.formValidationError'));
          return;
        }
        
        await this.$refs.reviewFormRef.validate();
        
        this.reviewLoading = true;
        
        // 映射审核结果到状态码
        const statusMap = {
          'approved': 1,
          'rejected': 2
        };
        
        // 调用新的审核接口
        const res = await reviewDiffUpgradeApply(this.currentApplication.id, {
          status: statusMap[this.reviewForm.reviewResult],
          remark: this.reviewForm.reviewComment,
          content: this.currentApplication.applyReason
        });
        
        if (res && res.code === 0) {
          ElMessage.success(this.$t('UpgradeApplyManagement.reviewSuccess'));
          this.closeReviewDialog();
          // 刷新数据
          this.$emit('refresh');
        } else {
          ElMessage.error(res && res.message ? res.message : this.$t('UpgradeApplyManagement.reviewFailed'));
        }
      } catch (error) {
        if (error !== false) { // 不是表单验证错误
          console.error('审核失败:', error);
          // 检查是否有错误消息，避免重复显示
          if (!(error && error.message === this.$t('UpgradeApplyManagement.reviewCommentMinLength'))) {
            ElMessage.error(this.$t('UpgradeApplyManagement.reviewFailed'));
          }
        }
      } finally {
        this.reviewLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.table-container {
  margin: 20px;
}

.action-buttons {
  display: flex;
  gap: 8px;
  justify-content: center;
  align-items: center;
}

.approve-btn {
  background: linear-gradient(135deg, #67c23a 0%, #85ce61 100%);
  border: none;
  box-shadow: 0 2px 4px rgba(103, 194, 58, 0.3);
  transition: all 0.3s ease;
}

.approve-btn:hover {
  background: linear-gradient(135deg, #5daf34 0%, #7bc85a 100%);
  box-shadow: 0 4px 8px rgba(103, 194, 58, 0.4);
  transform: translateY(-1px);
}

.reject-btn {
  background: linear-gradient(135deg, #f56c6c 0%, #f78989 100%);
  border: none;
  box-shadow: 0 2px 4px rgba(245, 108, 108, 0.3);
  transition: all 0.3s ease;
}

.reject-btn:hover {
  background: linear-gradient(135deg, #f45656 0%, #f77777 100%);
  box-shadow: 0 4px 8px rgba(245, 108, 108, 0.4);
  transform: translateY(-1px);
}

.status-display {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
}

.status-tag {
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 12px;
}

.view-btn {
  background: linear-gradient(135deg, #909399 0%, #a6a9ad 100%);
  border: none;
  box-shadow: 0 2px 4px rgba(144, 147, 153, 0.3);
  transition: all 0.3s ease;
}

.view-btn:hover {
  background: linear-gradient(135deg, #82848a 0%, #9ca0a4 100%);
  box-shadow: 0 4px 8px rgba(144, 147, 153, 0.4);
  transform: translateY(-1px);
}

.el-button {
  border-radius: 6px;
  font-weight: 500;
}

.el-button .el-icon {
  margin-right: 4px;
}
</style>
